import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { PollyEnv } from '../types';
import { isLocalhost, getCognitoBearerToken } from '../utils/auth';
import { CreateSchemaStateUploadUrlResponse, GetSchemaStateDownloadResponse, KnowledgeGraphInfo, KnowledgeGraphSchemaResponse, KnowledgeGraphSummaryResponse } from '../types/api-response';
import { Position } from '../types/erd';

const ELASTIC_ETL_PIPELINE_IDS: Record<PollyEnv, string> = {
  polly: '7f95a204-d2d3-463f-b163-7c4cd8024901',
  testpolly: '2faa79b1-910c-400a-ada7-63e4f09a2220',
  devpolly: 'effdeb6f-444a-4bac-87bb-849d3c385189',
};

/**
 * On deployed envs the app and API share .elucidata.io — withCredentials sends
 * Cognito cookies automatically. On localhost the CRA proxy injects the token.
 */
function getBaseUrl(pollyEnv: PollyEnv): string {
  if (isLocalhost()) return `/api/${pollyEnv}`;
  return `https://apis.${pollyEnv}.elucidata.io`;
}

class ApiService {
  private axiosInstance: AxiosInstance | null = null;
  private pollyEnv: PollyEnv | null = null;

  /**
   * Initialize with a Polly API key (localhost dev only).
   * Hits the API directly — no proxy needed, no cross-domain cookie issues.
   */
  initializeWithApiKey(pollyEnv: PollyEnv, apiKey: string) {
    this.pollyEnv = pollyEnv;
    this.axiosInstance = axios.create({
      baseURL: `https://apis.${pollyEnv}.elucidata.io`,
      headers: { 'x-api-key': apiKey },
    });
    this.axiosInstance.interceptors.request.use((config) => {
      const method = (config.method ?? 'get').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        config.headers['Content-Type'] = 'application/vnd.api+json';
      }
      return config;
    });
  }

  /**
   * Initialize with Cognito SSO cookie (deployed envs).
   * withCredentials sends the cookie; on401 fires once on session expiry.
   */
  initialize(pollyEnv: PollyEnv, on401?: () => void) {
    this.pollyEnv = pollyEnv;

    this.axiosInstance = axios.create({
      baseURL: getBaseUrl(pollyEnv),
    });

    // Inject auth + Content-Type on every request.
    // Content-Type is only set on requests that carry a body (POST/PUT/PATCH/DELETE)
    // — setting it on GET requests triggers a CORS preflight that the API gateway rejects.
    // Bearer token matches Atlas Manager: read access token from Cognito cookie.
    // On localhost getCognitoBearerToken() returns null — the CRA proxy injects it instead.
    this.axiosInstance.interceptors.request.use((config) => {
      const token = getCognitoBearerToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const method = (config.method ?? 'get').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        config.headers['Content-Type'] = 'application/vnd.api+json';
      }
      return config;
    });

    // Deduplicate: only fire on401 once even if multiple concurrent requests 401.
    let on401Fired = false;
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && on401 && !on401Fired) {
          on401Fired = true;
          on401();
        }
        return Promise.reject(error);
      }
    );
  }

  isInitialized(): boolean {
    return this.axiosInstance !== null;
  }

  reset() {
    this.axiosInstance = null;
    this.pollyEnv = null;
  }

  getEnv(): PollyEnv | null {
    return this.pollyEnv;
  }

  getElasticEtlPipelineId(): string | null {
    if (!this.pollyEnv) return null;
    return ELASTIC_ETL_PIPELINE_IDS[this.pollyEnv] ?? null;
  }

  private async request<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    if (!this.axiosInstance) {
      throw new Error('API not initialized. Please set Polly environment and API key.');
    }
    const response = await this.axiosInstance.request<T>({
      url: endpoint,
      ...config,
    });
    return response.data;
  }

  // KG APIs
  async listKGs(includeUnpublished: boolean = true) {
    return this.request(`/polly-kg/kg/?include_unpublished=${includeUnpublished}`, {
      method: 'GET',
    });
  }

  async getKG(kgId: string) {
    return this.request(`/polly-kg/kg/${kgId}`, {
      method: 'GET',
    });
  }

  async createKG(data: { kg_name: string; kg_description: string; data_version?: string; data_files: any; gds_enabled?: boolean }) {
    return this.request('/polly-kg/kg/', {
      method: 'POST',
      data: {
        data: {
          attributes: data,
        },
      },
    });
  }

  // Version APIs
  async listVersions(kgId: string, includeUnpublished: boolean = true) {
    return this.request(`/polly-kg/kg/${kgId}/versions/?include_unpublished=${includeUnpublished}`, {
      method: 'GET',
    });
  }

  async getVersion(kgId: string, versionId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'GET',
    });
  }

  async createVersion(kgId: string, data: { kg_description: string; data_version?: string; data_files: any; gds_enabled?: boolean }) {
    return this.request(`/polly-kg/kg/${kgId}/versions/`, {
      method: 'POST',
      data: {
        data: {
          attributes: data,
        },
      },
    });
  }

  async publishVersion(kgId: string, versionId: string, published: boolean) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'PATCH',
      data: {
        data: {
          attributes: { published },
        },
      },
    });
  }

  async updateVersionDescription(kgId: string, versionId: string, description: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'PATCH',
      data: {
        data: {
          attributes: { kg_description: description },
        },
      },
    });
  }

  async updateVersionDataVersion(kgId: string, versionId: string, dataVersion: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'PATCH',
      data: {
        data: {
          attributes: { data_version: dataVersion },
        },
      },
    });
  }

  async updateVersionAutoScaling(kgId: string, versionId: string, enabled: boolean) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'PATCH',
      data: {
        data: {
          type: 'kg',
          attributes: { auto_scaling_enabled: enabled },
        },
      },
    });
  }

  async deleteVersion(kgId: string, versionId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/`, {
      method: 'DELETE',
    });
  }

  // Instance APIs
  async getAllInstances() {
    return this.request('/polly-kg/instances/', {
      method: 'GET',
    });
  }

  async getInstanceTypes() {
    return this.request('/polly-kg/instances?configs=true', {
      method: 'GET',
    });
  }

  async listInstances(kgId: string, versionId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/instances/`, {
      method: 'GET',
    });
  }

  async createInstance(kgId: string, versionId: string, instanceType: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/instances/`, {
      method: 'POST',
      data: {
        data: {
          attributes: { instance_type: instanceType },
        },
      },
    });
  }

  async getInstance(kgId: string, versionId: string, instanceId: string) {
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}`,
      {
        method: 'GET',
      }
    );
  }

  async updateInstanceStatus(
    kgId: string,
    versionId: string,
    instanceId: string,
    desiredStatus?: 'RUNNING' | 'STOPPED' | 'TERMINATED',
    setDefault: boolean = false
  ) {
    const queryParam = setDefault ? '?set_default=true' : '';
    const data = desiredStatus
      ? {
          data: {
            attributes: { desired_status: desiredStatus },
          },
        }
      : { data: {} };

    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/status${queryParam}`,
      {
        method: 'POST',
        data,
      }
    );
  }

  async setDefaultInstance(kgId: string, versionId: string, instanceId: string) {
    return this.updateInstanceStatus(kgId, versionId, instanceId, undefined, true);
  }

  async getInstanceLogs(kgId: string, versionId: string, instanceId: string, nextToken?: string) {
    const queryParam = nextToken ? `?next_token=${encodeURIComponent(nextToken)}` : '';
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/logs${queryParam}`,
      {
        method: 'GET',
      }
    );
  }

  async getInstanceStatus(kgId: string, versionId: string, instanceId: string) {
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/status`,
      {
        method: 'GET',
      }
    );
  }

  // ES Index APIs
  async listESIndexes(kgId: string, versionId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/elastic/indexes`, {
      method: 'GET',
    });
  }

  async deleteESIndex(kgId: string, versionId: string, indexName: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/elastic/indexes/${indexName}`, {
      method: 'DELETE',
    });
  }

  async setESIndexAlias(kgId: string, versionId: string, indexName: string, aliasName: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/elastic/indexes/${indexName}/alias`, {
      method: 'POST',
      data: {
        data: {
          attributes: {
            alias_name: aliasName,
          },
        },
      },
    });
  }

  async queryESIndex(kgId: string, versionId: string, indexName: string, query: any) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/elastic/indexes/${indexName}/query`, {
      method: 'POST',
      data: query,
    });
  }

  // DB Index APIs
  async listDBIndexes(kgId: string, versionId: string, instanceId: string, indexType?: string) {
    const query = indexType ? `?index_type=${encodeURIComponent(indexType)}` : '';
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/db-indexes${query}`,
      { method: 'GET' }
    );
  }

  async createDBIndex(
    kgId: string,
    versionId: string,
    instanceId: string,
    nodeLabel: string,
    nodeProperties: string[],
    indexType: string = 'range'
  ): Promise<{ query_id: string | null }> {
    const res: any = await this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/db-indexes`,
      {
        method: 'POST',
        data: {
          data: {
            attributes: { node_label: nodeLabel, node_properties: nodeProperties, index_type: indexType },
          },
        },
      }
    );
    // Server may return a query_id for async polling, or complete synchronously with no body
    const queryId = res?.data?.id ?? null;
    return { query_id: queryId };
  }

  async deleteDBIndex(kgId: string, versionId: string, instanceId: string, indexName: string): Promise<{ query_id: string | null }> {
    const res: any = await this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/instances/${instanceId}/db-indexes/${encodeURIComponent(indexName)}`,
      { method: 'DELETE' }
    );
    // Server may return a query_id for async polling, or an empty/204 response if completed synchronously
    const queryId = res?.data?.id ?? null;
    return { query_id: queryId };
  }

  async pollIndexOperation(
    kgId: string,
    versionId: string,
    queryId: string,
    signal?: AbortSignal,
  ): Promise<{ status: string }> {
    const INTERVAL_MS = 2000;
    const TIMEOUT_MS  = 300_000;
    const deadline = Date.now() + TIMEOUT_MS;

    while (Date.now() < deadline) {
      if (signal?.aborted) throw new DOMException('Polling aborted', 'AbortError');

      const res: any = await this.request(
        `/polly-kg/kg/${kgId}/versions/${versionId}/queries/${queryId}/results?download=true`,
        { method: 'GET' }
      );

      if (signal?.aborted) throw new DOMException('Polling aborted', 'AbortError');

      const status = (res?.data?.attributes?.status ?? '').toUpperCase() as string;
      if (status === 'COMPLETED' || status === 'FAILED') return { status };

      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, INTERVAL_MS);
        signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Polling aborted', 'AbortError')); }, { once: true });
      });
    }

    throw new Error('Index operation timed out');
  }

  // Query APIs
  async submitQuery(
    kgId: string,
    versionId: string,
    queryString: string,
    instanceId?: string
  ) {
    const endpoint = instanceId
      ? `/polly-kg/kg/${kgId}/versions/${versionId}/queries?instance_id=${instanceId}`
      : `/polly-kg/kg/${kgId}/versions/${versionId}/queries`;

    return this.request(endpoint, {
      method: 'POST',
      data: {
        data: {
          attributes: { query_string: queryString },
        },
      },
    });
  }

  async executeSynchronousQuery(
    kgId: string,
    versionId: string,
    queryString: string,
    instanceId: string
  ) {
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/synchronous-queries?instance_id=${instanceId}`,
      {
        method: 'POST',
        data: {
          data: {
            type: 'kg_query',
            attributes: {
              query_string: queryString,
            },
          },
        },
      }
    );
  }

  async getQueryResults(kgId: string, versionId: string, queryId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/queries/${queryId}/results`, {
      method: 'GET',
    });
  }

  async listQueries(
    kgId: string,
    versionId: string,
    opts: {
      pageSize?:   number;
      nextToken?:  string;
      instanceId?: string;
      status?:     string;
      startDate?:  number;  // epoch seconds
      endDate?:    number;  // epoch seconds
      includeUserId?: boolean;
    } = {}
  ) {
    const params = new URLSearchParams();
    if (opts.status)     params.set('status',           opts.status);
    params.set('page_size', String(opts.pageSize ?? 100));
    if (opts.instanceId)    params.set('instance_id',     opts.instanceId);
    if (opts.startDate)     params.set('start_date',      String(opts.startDate));
    if (opts.endDate)       params.set('end_date',        String(opts.endDate));
    if (opts.nextToken)     params.set('next_token',      opts.nextToken);
    if (opts.includeUserId) params.set('include_user_id', 'true');
    return this.request(
      `/polly-kg/kg/${kgId}/versions/${versionId}/queries?${params.toString()}`,
      { method: 'GET' }
    );
  }

  // Custom Attributes APIs
  async getCustomAttributes(kgId: string, versionId: string) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/custom-attributes?description=true`, {
      method: 'GET',
    });
  }

  async updateCustomAttributes(kgId: string, versionId: string, customAttributes: any) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/custom-attributes`, {
      method: 'POST',
      data: {
        data: {
          attributes: { custom_attributes: customAttributes },
        },
      },
    });
  }

  // Schema and Summary
  async getSchema(kgId: string, versionId: string, refresh: boolean = false) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/schema?refresh=${refresh}`, {
      method: 'GET',
    });
  }

  async getSummary(kgId: string, versionId: string, refresh: boolean = false) {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/summary?refresh=${refresh}`, {
      method: 'GET',
    });
  }

  async getERDSchema(info: KnowledgeGraphInfo): Promise<KnowledgeGraphSchemaResponse> {
    return this.request(`/polly-kg/kg/${info.kg_id}/versions/${info.version_id}/schema?description=true`, { method: 'GET' });
  }

  async getERDSummary(info: KnowledgeGraphInfo): Promise<KnowledgeGraphSummaryResponse> {
    return this.request(`/polly-kg/kg/${info.kg_id}/versions/${info.version_id}/summary?stats=true`, { method: 'GET' });
  }

  async createErdSchemaStateUrl(kgId: string, versionId: string): Promise<CreateSchemaStateUploadUrlResponse> {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/states?schema_state=true`, {
      method: 'POST',
      data: {}
    });
  }

  async getErdStatDownloadUrl(kgId: string, versionId: string): Promise<GetSchemaStateDownloadResponse> {
    return this.request(`/polly-kg/kg/${kgId}/versions/${versionId}/states/${kgId}_${versionId}_schema_state`, { method: 'GET' });
  }

  async getErdSchemaState(downloadUrl: string,): Promise<Record<string, Position>> {
    const response: Response = await fetch(downloadUrl, { method: 'GET' });

    if (!response.ok) {
      throw new Error('Failed to fetch ERD schema state');
    }

    return (await response.json()) as Record<string, Position>;
  }

  async updateErdSchemaState(stateIdUrl: string, positions: Record<string, Position>,): Promise<void> {
    const response: Response = await fetch(stateIdUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(positions),
    });

    if (!response.ok) {
      throw new Error('Failed to upload ERD schema state');
    }
  }


  // User APIs
  async getUserInfo() {
    return this.request('/polly/user', {
      method: 'GET',
    });
  }

  async getCurrentUser(): Promise<{ id: string; firstName: string; lastName: string; email: string } | null> {
    try {
      const res: any = await this.getUserInfo();
      console.log('[getCurrentUser] raw:', JSON.stringify(res).slice(0, 400));
      // /polly/user returns various shapes — try all known paths
      const u     = res?.data ?? res;
      const attrs = u?.attributes ?? u ?? {};
      const id        = String(u?.id ?? attrs?.id ?? '');
      const firstName = String(attrs.first_name  ?? attrs.firstName  ?? attrs.name?.split(' ')[0] ?? '');
      const lastName  = String(attrs.last_name   ?? attrs.lastName   ?? attrs.name?.split(' ').slice(1).join(' ') ?? '');
      const email     = String(attrs.email ?? attrs.username ?? '');
      if (!id && !email) return null;
      return { id, firstName, lastName, email };
    } catch {
      return null;
    }
  }

  /**
   * Fetch /auth/users/me?include=roles — single call that returns:
   *   - allowed: whether the user has a required KG admin role
   *   - email, firstName, lastName: user profile extracted from the response
   * Throws on network error; returns allowed=false on 401/403.
   */
  async fetchUserProfile(): Promise<{
    allowed: boolean;
    hasGdsLicense: boolean;
    email: string;
    firstName: string;
    lastName: string;
  }> {
    const ALLOWED_ROLES = ['POLLY_KG_ORG_ADMIN', 'POLLY_KG_GDS_ORG_ADMIN'];
    const GDS_ROLE = 'POLLY_KG_GDS_ORG_ADMIN';
    const empty = { allowed: false, hasGdsLicense: false, email: '', firstName: '', lastName: '' };

    if (!this.axiosInstance) return empty;

    const data = await this.request<Record<string, unknown>>(
      '/auth/users/me?include=roles',
      { method: 'GET' }
    );

    // ── Extract user profile ─────────────────────────────────────────────────
    const dataNode = (data.data ?? {}) as Record<string, unknown>;
    const attrs = (dataNode.attributes ?? {}) as Record<string, unknown>;
    const email     = String(attrs.email ?? attrs.username ?? '');
    const firstName = String(attrs.first_name  ?? attrs.firstName  ?? '');
    const lastName  = String(attrs.last_name   ?? attrs.lastName   ?? '');

    // ── Check KG admin role ──────────────────────────────────────────────────
    const extractRoleName = (item: Record<string, unknown>): string => {
      const a = (item.attributes ?? {}) as Record<string, unknown>;
      return String(a.role_name ?? a.name ?? a.roleName ?? item.name ?? '');
    };

    const included = Array.isArray(data.included)
      ? (data.included as Record<string, unknown>[])
      : [];

    let allowed = false;

    // Shape 1: JSONAPI included array with type 'roles' or 'role'
    if (included.length > 0) {
      allowed = included.some(
        (item) => (item.type === 'roles' || item.type === 'role') && ALLOWED_ROLES.includes(extractRoleName(item))
      );
    }

    // Shape 2: roles array directly on data.attributes
    if (!allowed) {
      const directRoles = Array.isArray(attrs.roles) ? attrs.roles as Record<string, unknown>[] : [];
      allowed = directRoles.some(r => ALLOWED_ROLES.includes(extractRoleName(r)));
    }

    // Shape 3: relationships → roles → ids matched against included
    if (!allowed && included.length > 0) {
      const rels = (dataNode.relationships ?? {}) as Record<string, unknown>;
      const roleRelData = Array.isArray((rels.roles as any)?.data)
        ? (rels.roles as any).data as Record<string, unknown>[]
        : [];
      if (roleRelData.length > 0) {
        const allowedIds = new Set(roleRelData.map(r => String(r.id ?? '')));
        allowed = included.some(
          (item) =>
            (item.type === 'roles' || item.type === 'role') &&
            allowedIds.has(String(item.id ?? '')) &&
            ALLOWED_ROLES.includes(extractRoleName(item))
        );
      }
    }

    // ── Check GDS-specific license ───────────────────────────────────────────
    let hasGdsLicense = false;
    if (included.length > 0) {
      hasGdsLicense = included.some(
        (item) => (item.type === 'roles' || item.type === 'role') && extractRoleName(item) === GDS_ROLE
      );
    }
    if (!hasGdsLicense) {
      const directRoles = Array.isArray(attrs.roles) ? attrs.roles as Record<string, unknown>[] : [];
      hasGdsLicense = directRoles.some(r => extractRoleName(r) === GDS_ROLE);
    }
    if (!hasGdsLicense && included.length > 0) {
      const rels = (dataNode.relationships ?? {}) as Record<string, unknown>;
      const roleRelData = Array.isArray((rels.roles as any)?.data)
        ? (rels.roles as any).data as Record<string, unknown>[]
        : [];
      if (roleRelData.length > 0) {
        const allowedIds = new Set(roleRelData.map(r => String(r.id ?? '')));
        hasGdsLicense = included.some(
          (item) =>
            (item.type === 'roles' || item.type === 'role') &&
            allowedIds.has(String(item.id ?? '')) &&
            extractRoleName(item) === GDS_ROLE
        );
      }
    }

    return { allowed, hasGdsLicense, email, firstName, lastName };
  }

  /** Convenience wrapper used by App.tsx for the role gate. */
  async checkUserAuthorization(): Promise<boolean> {
    const { allowed } = await this.fetchUserProfile();
    return allowed;
  }

  // Workspace APIs
  async listWorkspaceFiles(
    workspaceId: string,
    parentId: string,
    documentIndex: string,
    workspaceIndex: string,
    from: number = 0,
    size: number = 20
  ) {
    return this.request(`/mithoo/workspaces/${workspaceId}/_search`, {
      method: 'POST',
      data: {
        from,
        size,
        sort: [{ 'entity_name.keyword': { order: 'asc' } }],
        query: {
          bool: {
            should: [
              {
                bool: {
                  must: [
                    { term: { _index: { value: documentIndex } } },
                    { term: { workspace_id: { value: workspaceId } } },
                    { term: { parent_id: { value: parentId } } },
                    {
                      bool: {
                        should: [
                          { terms: { entity_type: ['analysis_v2', 'notebook', 'file', 'folder'] } },
                        ],
                      },
                    },
                  ],
                },
              },
              {
                bool: {
                  must: [
                    { term: { _index: { value: workspaceIndex } } },
                    {
                      nested: {
                        path: 'permissions',
                        query: {
                          match: {
                            'permissions.user_id': { query: '{user_id}' },
                          },
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    });
  }

  // Pipeline / Pravaah APIs
  async searchBatches(pipelineId: string, orgId: string, searchTerm: string) {
    const params = new URLSearchParams({
      'page[size]': '50',
      'filter[pipeline_id]': pipelineId,
      'filter[org_id]': orgId,
      'filter[search_term]': searchTerm,
    });
    return this.request(`/pravaah/orchestration/batches?${params.toString()}`, {
      method: 'GET',
    });
  }

  async createBatch(pipelineId: string, orgId: string, name: string) {
    return this.request('/pravaah/orchestration/batches', {
      method: 'POST',
      data: {
        data: {
          type: 'batches',
          attributes: {
            customer_org: orgId,
            description: 'triggered from kg manager',
            name,
            pipeline_id: pipelineId,
            priority: 'high',
            tags: {},
          },
        },
      },
    });
  }

  async listRuns(batchId: string, pageSize: number = 5) {
    const params = new URLSearchParams({
      'filter[batch_id]': batchId,
      'page[size]': String(pageSize),
      'sort[field]': 'created_at',
      'sort[order]': 'desc',
    });
    return this.request(`/pravaah/orchestration/runs?${params.toString()}`, {
      method: 'GET',
    });
  }

  async createPipelineRun(batchId: string, runName: string, kgId: string, versionId: string, apiKey: string) {
    return this.request('/pravaah/orchestration/runs', {
      method: 'POST',
      data: {
        data: [
          {
            type: 'runs',
            attributes: {
              batch_id: batchId,
              config: { output: { export_dir: null } },
              name: runName,
              parameters: {
                kg_id: kgId,
                polly_api_key: apiKey,
                version_id: parseInt(versionId, 10),
              },
            },
          },
        ],
      },
    });
  }

  async searchWorkspaces(userId: string, from: number = 0, size: number = 20, searchTerm?: string) {
    let query;

    if (searchTerm && searchTerm.trim()) {
      // Search with term
      query = {
        bool: {
          must: [
            {
              nested: {
                path: 'permissions',
                query: {
                  match: {
                    'permissions.user_id': '{user_id}',
                  },
                },
              },
            },
            {
              bool: {
                should: [
                  {
                    query_string: {
                      default_field: 'name',
                      query: `*${searchTerm}*`,
                    },
                  },
                  {
                    nested: {
                      path: 'tags',
                      query: {
                        query_string: {
                          default_field: 'tags.tag_name',
                          query: `*${searchTerm}*`,
                        },
                      },
                    },
                  },
                ],
              },
            },
          ],
        },
      };
    } else {
      // No search term, use simple nested query
      query = {
        nested: {
          path: 'permissions',
          query: {
            match: {
              'permissions.user_id': '{user_id}',
            },
          },
        },
      };
    }

    return this.request('/mithoo/_search', {
      method: 'POST',
      data: {
        from,
        size,
        sort: [{ created_date: { order: 'desc' } }],
        query,
      },
    });
  }

  // ── Polly Co-Scientist APIs ───────────────────────────────────────────────────
  // Uses the same base URL pattern but /polly-co-scientist prefix.
  // Authentication: same X-Api-Key header already set on axiosInstance.

  // ── Polly Co-Scientist — uses the same axiosInstance (same base URL + api key) ──
  async coScientistCreateConversation(opts: {
    initialMessage: string;
    kgId: string;
    kgName: string;
    versionId: string;
  }): Promise<any> {
    return this.request('/polly-co-scientist/conversations/', {
      method: 'POST',
      data: {
        data: {
          type: 'conversations',
          attributes: {
            message: { content: opts.initialMessage },
            meta_data: { target_agent: 'knowledge-graph' },
            source: {
              kg_id: opts.kgId,
              kg_name: opts.kgName,
              version_id: parseInt(opts.versionId, 10),
            },
          },
        },
      },
    });
  }

  async coScientistSendMessage(conversationId: string, content: string): Promise<any> {
    return this.request(`/polly-co-scientist/conversations/${conversationId}/messages`, {
      method: 'POST',
      data: {
        data: {
          type: 'messages',
          attributes: { content },
        },
      },
    });
  }

  async coScientistGetMessage(conversationId: string, messageId: string): Promise<any> {
    return this.request(
      `/polly-co-scientist/conversations/${conversationId}/messages/${messageId}`,
      { method: 'GET' }
    );
  }

  async coScientistListMessages(conversationId: string, pageSize = 50): Promise<any> {
    return this.request(
      `/polly-co-scientist/conversations/${conversationId}/messages?page_size=${pageSize}`,
      { method: 'GET' }
    );
  }
}

export const apiService = new ApiService();
